import './ChartRenderer.scss';
import './PieRenderer.scss';
import cn from 'classnames';
import { useMemo } from 'react';
import { Cell, Label, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import {
  ChartConfig,
  FieldMetaCtx,
  QueryResponse
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { toPrettyPercent } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { ChartPlaceholder } from './ChartPlaceholder';
import { PreparedPie, PreparedPieSlice, preparePie } from './chart-renderer-utils';

interface PieRendererProps {
  config: ChartConfig;
  data: QueryResponse;
  className?: string;
  showTitle?: boolean;
  ctx?: FieldMetaCtx;
}

const formatShare = (share: number): string => toPrettyPercent(share, { decimals: 1 });

// A donut (the hole is fixed, not configurable) with the total in the center and a slice legend
// beside it. Slice math lives in preparePie; this only draws.
export const PieRenderer = ({
  config,
  data,
  className,
  showTitle = true,
  ctx
}: PieRendererProps) => {
  const result = useMemo(() => preparePie(config, data, ctx), [config, data, ctx]);
  if (!result.ok) return <ChartPlaceholder className={className} message={result.message} />;
  const { pie } = result;

  return (
    <div className={cn('chart-renderer', 'pie-renderer', className)}>
      {showTitle && <h3 className='chart-renderer-title'>{config.title}</h3>}
      {pie.notice && <div className='chart-renderer-notice'>{pie.notice}</div>}
      <div className='pie-renderer-body'>
        <div className='pie-renderer-canvas'>
          <ResponsiveContainer width='100%' height='100%'>
            <PieChart>
              <Pie
                data={pie.slices}
                dataKey='value'
                nameKey='label'
                innerRadius='60%'
                outerRadius='90%'
                stroke='white'
                strokeWidth={1}
                isAnimationActive={false}
              >
                {pie.slices.map(slice => (
                  <Cell key={slice.key} fill={slice.color} />
                ))}
                <Label position='center' content={<PieCenter pie={pie} />} />
              </Pie>
              <Tooltip content={<PieTooltip pie={pie} />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className='pie-renderer-legend'>
          {pie.slices.map(slice => (
            <li key={slice.key} className='pie-renderer-legend-row'>
              <span className='pie-renderer-swatch' style={{ backgroundColor: slice.color }} />
              <span className='pie-renderer-legend-label' title={slice.label}>
                {slice.label}
              </span>
              <span className='pie-renderer-legend-share'>{formatShare(slice.share)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// recharts clones the Label `content` element and injects the computed center as `x`/`y`.
const PieCenter = ({ pie, x = 0, y = 0 }: { pie: PreparedPie; x?: number; y?: number }) => (
  <text x={x} y={y} textAnchor='middle' className='pie-renderer-center'>
    <tspan x={x} dy='-0.2em' className='pie-renderer-center-value'>
      {pie.formatValue(pie.total)}
    </tspan>
    <tspan x={x} dy='1.4em' className='pie-renderer-center-label'>
      {pie.metricLabel}
    </tspan>
  </text>
);

interface PieTooltipProps {
  pie: PreparedPie;
  active?: boolean;
  payload?: { payload?: PreparedPieSlice }[];
}

// Reuses the cartesian chart tooltip's classes (ChartRenderer.scss) so both read the same.
const PieTooltip = ({ pie, active, payload }: PieTooltipProps) => {
  const slice = payload?.[0]?.payload;
  if (!active || !slice) return null;
  return (
    <div className='chart-renderer-tooltip'>
      <div className='chart-renderer-tooltip-label'>{slice.label}</div>
      <div className='chart-renderer-tooltip-row'>
        <span className='chart-renderer-tooltip-name'>{pie.metricLabel}</span>
        <span className='chart-renderer-tooltip-value'>{pie.formatValue(slice.value)}</span>
      </div>
      <div className='chart-renderer-tooltip-row'>
        <span className='chart-renderer-tooltip-name'>Share</span>
        <span className='chart-renderer-tooltip-value'>{formatShare(slice.share)}</span>
      </div>
    </div>
  );
};
